import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
// eslint-disable-next-line no-unused-vars
import { MathComponent } from "mathjax-react";

const D3fromD1 = () => {
  const title = "Calculation of D3 (Drekkana) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D3 chart, or Drekkana chart, is used in Vedic astrology to analyze
        siblings, courage, and strength. Below is the step-by-step process to
        calculate the D3 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D3 (Drekkana) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mars is located at 17° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Three Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into three equal parts: the first 10°, the
        second 10°, and the third 10°. The planet's degree will help determine
        which part it belongs to.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Assign the Drekkana Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 10°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 10°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 10°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mars at 17° Aries
      </Typography>

      <Typography variant="body1" paragraph>
        Aries is the D1 sign. Mars at 17° falls in the second 10° (between 10°
        and 20°). Therefore, it belongs to the 5th sign from Aries, which is
        Leo.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D3 (Drekkana) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D3
        (Drekkana) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_drekkana(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Drekkana the planet is in based on its degree
    if degree <= 10:
        # First 10° belongs to the same sign
        drekkana_sign = zodiac_signs[sign_index]
    elif 10 < degree <= 20:
        # Second 10° belongs to the 5th sign from the current sign
        drekkana_sign = zodiac_signs[(sign_index + 4) % 12]
    else:
        # Third 10° belongs to the 9th sign from the current sign
        drekkana_sign = zodiac_signs[(sign_index + 8) % 12]

    return drekkana_sign

# Example usage
planet = "Mars"
degree = 17
sign = "Aries"

drekkana_sign = calculate_drekkana(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {drekkana_sign} in the D3 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Drekkana for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        three equal parts and assigns the Drekkana based on the planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into three parts: the first 10°, the second
        10°, and the third 10°. It assigns the Drekkana based on the planet's
        degree within the sign:
        <br />
        - First 10° belongs to the same sign.
        <br />
        - Second 10° belongs to the 5th sign from the D1 sign.
        <br />- Third 10° belongs to the 9th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D3fromD1;
